// Plausible-looking fake patient identity for demos.
//
// Covers exactly the fields Anonymize touches — name, MRN, birth date, sex,
// the MRN's issuer and the referring physician — except it invents a person
// instead of blanking one. Nothing about the case itself (accession, study,
// specimen, equipment) and none of the technical fields are touched.

import type { MetadataDraft } from '../../features/upload/sidecar';

const FAMILY = [
  'Alvarado', 'Andersson', 'Bennett', 'Bianchi', 'Brennan', 'Castellanos',
  'Chowdhury', 'Delgado', 'Duarte', 'Ellingson', 'Escobar', 'Fairbanks',
  'Ferreira', 'Goodwin', 'Gruber', 'Halverson', 'Haddad', 'Ibarra',
  'Jankowski', 'Jimenez', 'Kaminski', 'Kimura', 'Kowalczyk', 'Laurent',
  'Lindqvist', 'Maalouf', 'Marchetti', 'Mbeki', 'Nakamura', 'Nieminen',
  'Okonkwo', 'Ortega', 'Pettersen', 'Pruitt', 'Quintana', 'Rasmussen',
  'Reyes', 'Rosenthal', 'Sandoval', 'Sinclair', 'Sorrentino', 'Tanaka',
  'Thibault', 'Torres', 'Ueda', 'Vasquez', 'Vogel', 'Whitfield',
  'Wickramasinghe', 'Yamamoto', 'Zielinski', 'Zamora',
];

const GIVEN = [
  'Adele', 'Amara', 'Anton', 'Bruno', 'Camille', 'Cato', 'Dario', 'Dilara',
  'Elena', 'Emeka', 'Esben', 'Felix', 'Freya', 'Greta', 'Hana', 'Hugo',
  'Ines', 'Ivo', 'Jonas', 'Juno', 'Karin', 'Kenji', 'Lars', 'Leila',
  'Mateo', 'Mireia', 'Nadia', 'Noor', 'Odalys', 'Oskar', 'Paloma', 'Piet',
  'Rafael', 'Rania', 'Rosa', 'Saskia', 'Silas', 'Tomas', 'Tove', 'Ursula',
  'Valentina', 'Viktor', 'Wren', 'Xiomara', 'Yara', 'Yusuf', 'Zoltan', 'Zuri',
];

const PHYSICIAN_FAMILY = [
  'Arbeláez', 'Baumgartner', 'Behrens', 'Cheng', 'Costa', 'Delacroix',
  'Eriksen', 'Farooqi', 'Fontaine', 'Grimaldi', 'Hoffmann', 'Iqbal',
  'Jarvis', 'Kovacs', 'Larsson', 'Lindgren', 'Moreau', 'Nwosu',
  'Petrov', 'Rasmussen', 'Salcedo', 'Takahashi', 'Vermeulen', 'Whitlock',
];

const pick = <T,>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];
const int = (min: number, max: number) => min + Math.floor(Math.random() * (max - min + 1));
const digits = (n: number) => String(int(0, 10 ** n - 1)).padStart(n, '0');
const letters = (n: number) =>
  Array.from({ length: n }, () => String.fromCharCode(65 + int(0, 25))).join('');
const pad = (n: number) => String(n).padStart(2, '0');

/** MRN styles differ by institution; pick a shape, not just a digit count. */
const MRN_STYLES = [
  () => `MRN-${digits(7)}`,
  () => digits(8),
  () => `${letters(2)}${digits(6)}`,
  () => `${digits(3)}-${digits(2)}-${digits(4)}`,
  () => `P${digits(9)}`,
];

/** A made-up patient, in the shape a real one would arrive in. */
export function randomDraft(): Partial<MetadataDraft> {
  const birth = new Date(int(1932, 2010), int(0, 11), int(1, 28));

  return {
    patientFamily: pick(FAMILY),
    patientGiven: pick(GIVEN),
    patientId: pick(MRN_STYLES)(),
    patientBirthDate: `${birth.getFullYear()}-${pad(birth.getMonth() + 1)}-${pad(birth.getDate())}`,
    patientSex: pick(['F', 'M', 'O'] as const),
    // Cleared for the same reason Anonymize clears it: a real issuer next to
    // an invented MRN would be a claim about a place that never saw this
    // patient.
    patientIdIssuer: '',
    referringPhysician: `${pick(PHYSICIAN_FAMILY)}, ${pick(GIVEN)}`,
  };
}
